
import {vec3} from '../utils/matrix';
import MapSurfaceTree from './surface-tree';
import MapResourceTree from './resource-tree';
import MapSrs from './srs';
import MapCache from './cache';
import MapConvert from './convert';
import MapMeasure from './measure';
import MapDraw from './draw';
import MapLoader from './loader/loader';
import MapStats from './stats';
import MapUrl from './url';
import * as Illumination from './illumination';
import Atmosphere from './atmosphere';
import MapStyle from './style';
import MapTrajectory from './trajectory';
import MapFreeLayer from './free-layer';
import MapGeodataBuilder from './geodata-builder';


var Map = function(core, path, config, bus) {

    // the store's live value map, shared with core and renderer;
    // values arrive already normalized
    this.config = config || {};
    this.core = core;

    // event bus owned by the typed `Map`; geo-feature events publish
    // through it
    this.bus = bus;

    // config watchers apply the side effects of live key changes;
    // unsubscribed in kill()
    this.configUnsubscribes = [
        core.configStore.watch(
            [
                'mapCache', 'mapGPUCache', 'mapMetatileCache',
                'mapPixelRatioUse',
            ],
            this.setupCache.bind(this)),
        core.configStore.watch(
            [
                'mapTraversalMaskThreshold', 'mapTraversalMaskErosion',
                'mapStructuralDescentBrake', 'mapShadingLambertian',
                'mapShadingSlope', 'mapShadingAspect', 'mapFlagLighting',
                'mapFlagNormalMaps', 'mapFlagDiffuseMaps',
                'mapFlagSpecularMaps', 'mapFlagBumpMaps',
                'mapFlagAtmosphere', 'mapFlagShadows', 'mapFlagLabels',
                'mapFlagVerticalExaggeration',
                'mapTexelSizeFit',
            ],
            this.markDirty.bind(this)),
        // locale and CSS DPI are baked into worker-generated geometry;
        // re-send the stylesheets so processors rebuild it at that scale
        core.configStore.watch(
            ['mapI18n', 'rendererCssDpi'],
            this.refreshGeodataStylesheets.bind(this)),
    ];
    this.killed = false;
    this.config = config || {};
    this.loaderSuspended = false;

    this.url = new MapUrl(this, path);

    this.srses = {};
    this.bodies = {};
    this.atmosphere = null;
    this.referenceFrame = {};
    this.services = {};
    this.credits = {};
    this.creditsByNumber = {};
    this.freeLayers = {};
    this.stylesheets = {};
    this.processingTasks = [];
    this.processingTasks2 = [];
    this.geodataProcessors = [];

    this.viewCounter = 0;
    this.srsReady = false;

    this.freeLayerSequence = [];

    this.visibleCredits = {
        imagery : {},
        mapdata : {}
    };
    
    this.metanodeBuffer = new Uint8Array(1024);

    var budgets = this.core.cacheBudgets;

    this.gpuCache = new MapCache(budgets.gpu);
    this.resourcesCache = new MapCache(budgets.resource);
    this.metatileCache = new MapCache(this.config.mapMetatileCache*1024*1024);

    this.loader = new MapLoader(this, this.config.mapDownloadThreads);

    this.renderer = this.core.renderer;

    this.stats = new MapStats(this);
    this.resourcesTree = new MapResourceTree(this);

    this.clickEvent = null;
    this.hoverEvent = null;
    this.hoverFeature = null;
    this.hoverFeatureId = null;
    this.lastHoverFeature = null;
    this.lastHoverFeatureId = null;
    this.hoverFeatureCounter = 0;
    this.hoverFeatureList = [];

    this.measure = null;
}

Map.prototype.kill = function() {
    this.killed = true;

    for (var i = 0; i < this.configUnsubscribes.length; i++) {
        this.configUnsubscribes[i]();
    }
    this.configUnsubscribes = [];

    for (var key in this.freeLayers) {
        var layer = this.freeLayers[key];
        if (layer && layer.tree) {
            layer.tree.kill();
        }
    }

    this.gpuCache.clear();
    this.resourcesCache.clear();
    this.metatileCache.clear();

    if (this.atmosphere != null) {
        this.atmosphere[Symbol.dispose]();
        this.atmosphere = null;
    }

    this.renderer = null;
};


/** Re-reads the cache budgets after a canvas or config change. */
Map.prototype.setupCache = function() {
    if (!this.resourcesCache) return;

    var budgets = this.core.cacheBudgets;

    this.resourcesCache.setMaxCost(budgets.resource);
    this.gpuCache.setMaxCost(budgets.gpu);
    this.metatileCache.setMaxCost(this.config.mapMetatileCache*1024*1024);
};


Map.prototype.setOption = function(/*key, value*/) {
};


Map.prototype.getOption = function(/*key*/) {
};


Map.prototype.addSrs = function(id, srs) {
    this.srses[id] = srs;
};


Map.prototype.getSrs = function(srsId) {
    return this.srses[srsId];
};


Map.prototype.getSrses = function() {
    return this.getMapKeys(this.srses);
};


Map.prototype.getSrsInfo = function(srsId) {
    var srs = this.getSrs(srsId);
    return srs ? srs.getInfo() : {};
};


Map.prototype.addBody = function(id, body) {
    this.bodies[id] = body;
};


Map.prototype.getBody = function(id) {
    return this.bodies[id];
};


Map.prototype.getBodies = function() {
    return this.getMapKeys(this.bodies);
};


Map.prototype.setReferenceFrame = function(referenceFrame) {
    this.referenceFrame = referenceFrame;
};


Map.prototype.getReferenceFrame = function() {
    return this.referenceFrame.getInfo();
};


Map.prototype.addCredit = function(id, credit) {
    this.credits[id] = credit;
    this.creditsByNumber[credit.id] = credit;
    credit.key = id;
};


Map.prototype.getCreditByNumber = function(id) {
    return this.creditsByNumber[id];
};


Map.prototype.getCreditById = function(id) {
    return this.credits[id];
};


Map.prototype.getCredits = function() {
    return this.getMapKeys(this.credits);
};


Map.prototype.getVisibleCredits = function() {
    var imagery = this.visibleCredits.imagery;
    var imageryArray = [];
    var imagerySpecificity = [];
    var i, li, t, sorted;

    for (var key in imagery) {
        imageryArray.push(key);
        imagerySpecificity.push(imagery[key]); 
    }

    //sort imagery
    do {
        sorted = true;
        
        for (i = 0, li = imagerySpecificity.length - 1; i < li; i++) {
            if (imagerySpecificity[i] < imagerySpecificity[i+1]) {
                t = imagerySpecificity[i];
                imagerySpecificity[i] = imagerySpecificity[i+1];
                imagerySpecificity[i+1] = t;
                t = imageryArray[i];
                imageryArray[i] = imageryArray[i+1];
                imageryArray[i+1] = t;
                sorted = false;
            } 
        }
        
    } while(!sorted);

    var mapdata = this.visibleCredits.mapdata;
    var mapdataArray = []; 
    var mapdataSpecificity = []; 

    for (key in mapdata) {
        mapdataArray.push(key);
        mapdataSpecificity.push(mapdata[key]); 
    }
    
    //sort imagery
    do {
        sorted = true;
        
        for (i = 0, li = mapdataSpecificity.length - 1; i < li; i++) {
            if (mapdataSpecificity[i] < mapdataSpecificity[i+1]) {
                t = mapdataSpecificity[i];
                mapdataSpecificity[i] = mapdataSpecificity[i+1];
                mapdataSpecificity[i+1] = t;
                t = mapdataArray[i];
                mapdataArray[i] = mapdataArray[i+1];
                mapdataArray[i+1] = t;
                sorted = false;
            } 
        }
        
    } while(!sorted);

    return {
        '3D' : [], 
        'imagery' : imageryArray, 
        'mapdata' : mapdataArray 
    };
};


// The only free-layer source kinds are 'vector' and 'vector-tiles',
// also under their VTS names 'geodata' and 'geodata-tiles'. URL-backed
// layers are checked once loaded in MapFreeLayer's onLoaded
// (free-layer.js).
function isRecognizedFreeLayerType(layer) {
    if (typeof layer === 'string') return true;
    if (layer instanceof MapFreeLayer) return !layer.ready || layer.geodata;
    if (layer == null || typeof layer !== 'object') return false;
    return ['vector', 'vector-tiles', 'geodata', 'geodata-tiles']
        .includes(layer['type']);
}


Map.prototype.addFreeLayer = function(id, layer) {

    if (layer == null) return;

    if (!isRecognizedFreeLayerType(layer)) {
        const type = layer && typeof layer === 'object'
            ? layer['type'] : typeof layer;
        console.warn(`addFreeLayer("${id}"): unsupported free `
            + `layer type "${type}"; not registered.`);
        return;
    }

    if (!(layer instanceof MapFreeLayer)) {
        layer = new MapFreeLayer(this, layer);
    }

    this.freeLayers[id] = layer;
    this.markDirty();
};


Map.prototype.removeFreeLayer = function(id) {
    if (this.freeLayers[id]) {
        this.freeLayers[id].kill();
        this.freeLayers[id] = null;
        this.markDirty();
    }
};


Map.prototype.getFreeLayer = function(id) {
    return this.freeLayers[id];
    //return this.searchArrayById(this.freeLayers, id);
};


Map.prototype.getFreeLayers = function() {
    var keys = [];
    for (var key in this.freeLayers) {
        keys.push(key);
    }
    return keys;    
};


Map.prototype.getMapsSrs = function(srs) {
    if (srs == null) {
        return null;
    }

    //is it proj4 string?
    if (srs.indexOf('+proj') != -1) {
        return new MapSrs(this, {'srsDef':srs});
    }

    //search existing srs
    return this.srses[srs];
};


Map.prototype.addStylesheet = function(id, style) {
    this.stylesheets[id] = style;
};


Map.prototype.getStylesheet = function(id) {
    return this.stylesheets[id];
    //return this.searchArrayById(this.stylesheets, id);
};


Map.prototype.getStylesheets = function() {
    var keys = [];

    for (var key in this.stylesheets) {
        keys.push(key);
    }
    return keys;
};


Map.prototype.getStylesheetData = function(id) {
    var stylesheet = this.getStylesheet(id);

    if (stylesheet) {
        return {'url':stylesheet.url, 'data': stylesheet.data};
    }
    
    return {'url':null, 'data':{}};
};


Map.prototype.setStylesheetData = function(id, data) {
    var stylesheet = this.getStylesheet(id);
    
    //if (stylesheet) {
      //  stylesheet.data = data;
    //}

    this.renderer.draw.clearJobHBuffer();

    if (stylesheet) {
        if (data) {
            stylesheet.setData(data);
        }

        for (var key in this.freeLayers) {
            var freeLayer = this.getFreeLayer(key);
            if (freeLayer && freeLayer.geodata && freeLayer.stylesheet == stylesheet) {
                
                if (freeLayer.geodataProcessor) {
                    freeLayer.geodataProcessor.setStylesheet(freeLayer.stylesheet);
                }

                freeLayer.geodataCounter++;
            }
        }
    }

    this.markDirty();
        
    //TODO: reset geodatview in free layers
};

/* Re-sends every free layer's stylesheet to its geodata processor
 * and invalidates processed geodata, so the worker rebuilds label
 * text from the current config (units, language). */
Map.prototype.refreshGeodataStylesheets = function() {
    this.renderer.draw.clearJobHBuffer();

    for (var key in this.freeLayers) {
        var freeLayer = this.getFreeLayer(key);
        if (freeLayer && freeLayer.geodata && freeLayer.geodataProcessor) {
            freeLayer.geodataProcessor.setStylesheet(freeLayer.stylesheet);
            freeLayer.geodataCounter++;
        }
    }

    this.markDirty();
};


Map.prototype.refreshView = function() {
    this.viewCounter++;

    if (this.outerMap.style)
        this.outerMap.style.refreshFreeLayerSequence();

    this.markDirty();
};


Map.prototype.searchArrayIndexById = function(array, id) {
    for (var i = 0, li = array.length; i < li; i++) {
        if (array[i].id == id) {
            return i;
        }
    }

    return -1;
};


Map.prototype.searchArrayById = function(array, id) {
    for (var i = 0, li = array.length; i < li; i++) {
        if (array[i].id == id) {
            return array[i];
        }
    }

    return null;
};


Map.prototype.searchMapByInnerId = function(map, id) {
    for (var key in map) {
        if (map[key].id == id) {
            return map[key];
        }
    }

    return null;
};


Map.prototype.getMapKeys = function(map) {
    var keys = [];
    for (var key in map) {
        keys.push(key);
    }

    return keys;
};


Map.prototype.getMapIds = function(map) {
    var keys = [];
    for (var key in map) {
        keys.push(key.id);
    }

    return keys;
};


Map.prototype.isReferenceFrameReady = function() {
    return this.referenceFrame.model.physicalSrs.isReady() &&
           this.referenceFrame.model.publicSrs.isReady() &&
           this.referenceFrame.model.navigationSrs.isReady();
};


Map.prototype.getPhysicalSrs = function() {
    return this.referenceFrame.model.physicalSrs;
};


Map.prototype.getPublicSrs = function() {
    return this.referenceFrame.model.publicSrs;
};


Map.prototype.getNavigationSrs = function() {
    return this.referenceFrame.model.navigationSrs;
};


Map.prototype.getCurrentCredits = function() {

    return this.getVisibleCredits();
};


Map.prototype.getCreditInfo = function(creditId) {

    var credit = this.getCreditById(creditId);
    return credit ? credit.getInfo() : {};
};


Map.prototype.convertCoords = function(sourceSrs, destinationSrs, coords) {

    var srs = this.getSrs(sourceSrs);
    var srs2 = this.getSrs(destinationSrs);
    if (!srs || !srs2) return null;

    return srs2.convertCoordsFrom(coords, srs);
};


Map.prototype.convertCoordsFromNavToPublic = function(pos) {

    return this.convert.getPublicCoords(pos);
};


Map.prototype.convertCoordsFromPublicToNav = function(pos) {

    return this.convert.getNavCoordsFromPublic(pos);
};


Map.prototype.convertCoordsFromNavToPhys = function(pos, includeSE) {

    return this.convert.getPhysCoords(pos, includeSE);
};


Map.prototype.convertCoordsFromPhysToNav = function(pos, containsSE) {

    return this.convert.convertCoordsFromPhysToNav(pos, containsSE);
};


Map.prototype.convertCoordsFromNavToCanvas = function(pos) {

    return this.convert.getCanvasCoords(pos);
};


Map.prototype.convertCoordsFromPhysToCanvas = function(pos, containsSE) {

    return this.convert.getCanvasCoords(pos, true, containsSE);
};


Map.prototype.convertCoordsFromNavToCameraSpace = function(pos) {

    return this.convert.getCameraSpaceCoords(pos);
};


Map.prototype.convertCoordsFromPhysToCameraSpace = function(pos) {

    var p = this.outerMap.camera.geometry.eye;
    return [pos[0] - p[0], pos[1] - p[1], pos[2] - p[2]];
};


Map.prototype.transformPhysCoordsBySE = function(pos) {

    return this.convert.transformPhysCoordsBySE(pos);
};


Map.prototype.getGeodesicLinePoints = function(
    coords, coords2, height, density) {

    return this.convert.getGeodesicLinePoints(
        coords, coords2, height, density);
};


Map.prototype.getDistance = function(
    coords, coords2, includingHeights, usePublic) {

    return this.measure.getDistance(
        coords, coords2, includingHeights, usePublic);
};


Map.prototype.getAzimuthCorrection = function(coords, coords2) {

    return this.measure.getAzimuthCorrection(coords, coords2);
};


Map.prototype.generateTrajectory = function(target, options) {

    return (new MapTrajectory(this, this.outerMap.camera, target, options))
        .generate();
};


Map.prototype.redraw = function() {

    this.markDirty();
    return this;
};


Map.prototype.setLoaderSuspended = function(state) {

    this.loaderSuspended = state;
    return this;
};


Map.prototype.getLoaderSuspended = function() {

    return this.loaderSuspended;
};


Map.prototype.getGpuCache = function() {

    return this.gpuCache;
};


Map.prototype.getStats = function(switches) {

    if (switches) {

        return {
            'maxZoom' : this.outerMap.overrides.maxZoom
        };
    }

    var busyWorkers = 0;
    for (var i = 0, li = this.geodataProcessors.length; i < li; i++) {

        if (this.geodataProcessors[i].busy) busyWorkers++;
    }

    return {
        'bestMeshTexelSize' : this.bestMeshTexelSize,
        'bestGeodataTexelSize' : this.bestGeodataTexelSize,
        'downloading' : this.loader.downloading.length,
        'lastDownload' : this.loader.lastDownloadTime,
        'surfaces' : this.outerMap.surfaceList().length,
        'freeLayers' : this.freeLayerSequence.length,
        'texelSizeFit' : this.config.mapTexelSizeFit,
        'processingTasks' : this.processingTasks.length,
        'busyWorkers' : busyWorkers,
        'dirty' : this.dirty,
        'drawnTiles' : this.stats.drawnTiles,
        'drawnGeodataTiles' : this.stats.drawnGeodataTiles,
        'renderTime' : this.stats.rendererTime,
        'frameTime' : this.stats.frameTime
    };
};


Map.prototype.createGeodata = function() {

    return new MapGeodataBuilder(this);
};


Map.prototype.getGeodataGeometry = function(id) {

    return this.renderer.geometries[id];
};


Map.prototype.setGeodataSelection = function(selection) {

    this.renderer.geodataSelection = selection;
    this.markDirty();
    return this;
};


Map.prototype.getGeodataSelection = function() {

    return this.renderer.geodataSelection;
};


Map.prototype.click = function(screenX, screenY, state) {
    this.clickEvent = [screenX, screenY, state];
};


Map.prototype.hover = function(screenX, screenY, persistent, state) {
    this.hoverEvent = [screenX, screenY, persistent, state];
};


Map.prototype.markDirty = function() {
    this.dirty = true;
    this.hitMapDirty = true;
    this.geoHitMapDirty = true;
};


Map.prototype.getScreenRay = function(screenX, screenY) {
    return this.renderer.getScreenRay(screenX, screenY);
};


Map.prototype.getScreenDepth = function(
    screenX, screenY, dilate = 0, useGeometricIntersection = false,
    coordinateSpace = 'layout') {

    if (useGeometricIntersection) {

        var cameraPos = this.outerMap.camera.geometry.eye;
        var ray = this.renderer.getScreenRay(
            screenX, screenY, coordinateSpace), a, d;

        if (this.getNavigationSrs().isProjected()) { //plane fallback
            var planePos = [0,0,Math.min(-1000,this.referenceFrame.getGlobalHeightRange()[0])];
            var planeNormal = [0,0,1];

            d = vec3.dot(planeNormal, ray); //minification is wrong there
            a = [planePos[0] - cameraPos[0], planePos[1] - cameraPos[1], planePos[2] - cameraPos[2]];
            t = vec3.dot(a, planeNormal) / d;

            if (t >= 0) {
                return [true, t];
            } else {
                return [false, 1];
            }

        } else { //elipsoid fallback
            var navigationSrsInfo = this.getNavigationSrs().getSrsInfo();
            var planetRadius = navigationSrsInfo['b'] + this.referenceFrame.getGlobalHeightRange()[0];
        
            var offset = [cameraPos[0], cameraPos[1], cameraPos[2]];
            a = vec3.dot(ray, ray); //minification is wrong there
            var b = 2 * vec3.dot(ray, offset);
            var c = vec3.dot(offset, offset) - planetRadius * planetRadius;
            d = b * b - 4 * a * c;
            
            if (d > 0) {
                d = Math.sqrt(d);
                var t1 = (-b - d) / (2*a);
                var t2 = (-b + d) / (2*a);
                var t = (t1 < t2) ? t1 : t2;

                return [true, t];
            } else {
                return [false, 1];
            }
        }

    } else {

        if (this.hitMapDirty) this.outerMap.updateDepthHitmap();

        var res = this.renderer.getDepth(
            screenX, screenY, dilate, coordinateSpace);
    }

    return res;
};


Map.prototype.getHitCoords = function(screenX, screenY) {
    if (this.hitMapDirty) {
        this.outerMap.updateDepthHitmap();
    }

    var cameraSpaceCoords = this.renderer.hitTest(screenX, screenY);

    var fallbackUsed = false;
    var cameraPos = this.outerMap.camera.geometry.eye;
    var worldPos;

    var ray = cameraSpaceCoords[4], a, d;

    if (this.getNavigationSrs().isProjected()) { //plane fallback
        var planePos = [0,0,Math.min(-1000,this.referenceFrame.getGlobalHeightRange()[0])];
        var planeNormal = [0,0,1];

        d = vec3.dot(planeNormal, ray); //minification is wrong there
        //if (d > 1e-6) {
        a = [planePos[0] - cameraPos[0], planePos[1] - cameraPos[1], planePos[2] - cameraPos[2]];
        t = vec3.dot(a, planeNormal) / d;
            
            //var t = (vec3.dot(cameraPos, planeNormal) + (-500)) / d;            
        if (t >= 0) {
            if (!cameraSpaceCoords[3] || t < cameraSpaceCoords[5]) {
                worldPos = [ (ray[0] * t) + cameraPos[0],
                    (ray[1] * t) + cameraPos[1],
                    (ray[2] * t) + cameraPos[2] ];
    
                fallbackUsed = true;
            }
        }
        //}

    } else /*if (false)*/ { //elipsoid fallback
        var navigationSrsInfo = this.getNavigationSrs().getSrsInfo();
        var planetRadius = navigationSrsInfo['b'] + this.referenceFrame.getGlobalHeightRange()[0];
    
        var offset = [cameraPos[0], cameraPos[1], cameraPos[2]];
        a = vec3.dot(ray, ray); //minification is wrong there
        var b = 2 * vec3.dot(ray, offset);
        var c = vec3.dot(offset, offset) - planetRadius * planetRadius;
        d = b * b - 4 * a * c;
        
        if (d > 0) {
            d = Math.sqrt(d);
            var t1 = (-b - d) / (2*a);
            var t2 = (-b + d) / (2*a);
            var t = (t1 < t2) ? t1 : t2;

            //console.log("hit: " + t + ",   " + cameraSpaceCoords[5]);
            
            if (!cameraSpaceCoords[3] || t < cameraSpaceCoords[5]) {
                worldPos = [ (ray[0] * t) + cameraPos[0],
                    (ray[1] * t) + cameraPos[1],
                    (ray[2] * t) + cameraPos[2] ];

                fallbackUsed = true;
            }
        }   
    }
    
    if (!cameraSpaceCoords[3] && !fallbackUsed) {
        return null;
    }
    
    if (!fallbackUsed) {
        worldPos = [ cameraSpaceCoords[0] + cameraPos[0],
            cameraSpaceCoords[1] + cameraPos[1],
            cameraSpaceCoords[2] + cameraPos[2] ];
    }

    var navCoords = this.convert.convertCoords(worldPos, 'physical', 'navigation');

    navCoords[2] = this.renderer.getUnsuperElevatedHeight(
        navCoords[2], this.outerMap.selectionCamera.viewExtent);

    return navCoords;
};


Map.prototype.hitTestGeoLayers = function(screenX, screenY, mode) {
    var labelsEnabled = this.outerMap.overrides.flagLabels
        ?? this.config.mapFlagLabels;

    if (!labelsEnabled) {
        this.lastHoverFeature = null;
        this.lastHoverFeatureId = null;
        this.hoverFeature = null;
        this.hoverFeatureId = null;

        return [null, false, []];
    }

    if (this.geoHitMapDirty) {
        if (this.freeLayerSequence.length > 0) {
            this.draw.drawGeodataHitmap();
        }
    }

    if (this.freeLayerSequence.length === 0) {
        this.lastHoverFeature = null;
        this.lastHoverFeatureId = null;
        this.hoverFeature = null;
        this.hoverFeatureId = null;

        return [null, false, []];
    }

    var res = this.renderer.hitTestGeoLayers(screenX, screenY);
    var relatedEvents, elementIndex;

    if (res[0]) { //do we hit something?
        //console.log(JSON.stringify([id, JSON.stringify(this.hoverFeatureList[id])]));
       
        var id = (res[1]) + (res[2]<<8);
		
        var feature = this.hoverFeatureList[id];

        if (!feature) {
            return [null, false, [], elementIndex];
        }

        if (feature[6]) { //advanced hit feature?
            res = this.renderer.hitTestGeoLayers(screenX, screenY, true);
        
            if (res[0]) { //do we hit something?
                elementIndex = (res[1]) + (res[2]<<8);
            }
        }

        if (mode == 'hover') {
            this.lastHoverFeature = this.hoverFeature;
            this.lastHoverFeatureId = this.hoverFeatureId;
            
            if (feature && feature[3]) {
                this.hoverFeature = feature;
                this.hoverFeatureId = (feature != null) ? feature[0]['#id'] : null;
            } else {
                this.hoverFeature = null;
                this.hoverFeatureId = null;
            }

            relatedEvents = [];

            if (this.hoverFeatureId != this.lastHoverFeatureId) {
                if (this.lastHoverFeatureId != null) {
                    relatedEvents.push(['leave', this.lastHoverFeature, this.lastHoverFeatureId]);
                }

                if (this.hoverFeatureId != null) {
                    relatedEvents.push(['enter', this.hoverFeature, this.hoverFeatureId]);
                }

                this.dirty = true;
            }

            if (this.hoverFeature != null && this.hoverFeature[3]) {
                return [this.hoverFeature, true, relatedEvents, elementIndex];
            } else {
                return [null, false, relatedEvents, elementIndex];
            }
        }

        if (mode == 'click') {
            if (feature != null && feature[2]) {
                return [feature, true, [], elementIndex];
            } else {
                return [null, false, [], elementIndex];
            }
        }
    } else {
        relatedEvents = [];

        if (mode == 'hover') {
            this.lastHoverFeature = this.hoverFeature;
            this.lastHoverFeatureId = this.hoverFeatureId;
            this.hoverFeature = null;
            this.hoverFeatureId = null;

            if (this.lastHoverFeatureId != null) {
                if (this.lastHoverFeatureId != null) {
                    relatedEvents.push(['leave', this.lastHoverFeature, this.lastHoverFeatureId]);
                }

                this.dirty = true;
            }
        }

        return [null, false, relatedEvents, elementIndex];
    }
};

Map.prototype.applyCredits = function(tile) {
    var value, value2;
    for (var key in tile.imageryCredits) {
        value = tile.imageryCredits[key];
        value2 = this.visibleCredits.imagery[key];

        if (value2) {
            this.visibleCredits.imagery[key] = value > value2 ? value : value2;
        } else {
            this.visibleCredits.imagery[key] = value;
        }
    }
    for (key in tile.mapdataCredits) {
        value = tile.mapdataCredits[key];
        value2 = this.visibleCredits.mapdata[key];

        if (value2) {
            this.visibleCredits.mapdata[key] = value > value2 ? value : value2;
        } else {
            this.visibleCredits.mapdata[key] = value;
        }
    }    
};


/* Loader workers post completed tile data back to the main thread via
 * onmessage. Those handlers push onLoaded callbacks here instead of
 * running them directly, so that tile-tree mutations and GPU uploads
 * always happen at a known point inside the render loop rather than
 * in a raw message handler that could fire between any two statements. */
Map.prototype.processProcessingTasks = function() {
    while (this.processingTasks.length > 0) {
        if (this.stats.renderBuild > this.config.mapMaxProcessingTime) {
            this.markDirty();
            return;
        }

        this.processingTasks[0]();
        this.processingTasks.shift();
    }

    var deadline = performance.now()
        + this.config.mapMaxGeodataProcessingTime;

    while (this.processingTasks2.length > 0) {
        if (performance.now() >= deadline) {
            this.markDirty();
            return;
        }

        if (this.processingTasks2[0](deadline) != -123) {
            this.processingTasks2.shift();
        } else {
            break;
        }
    }

};


Map.prototype.addProcessingTask = function(task) {
    this.processingTasks.push(task);
};

Map.prototype.addProcessingTask2 = function(task) {
    this.processingTasks2.push(task);
};


/* Residual pre-draw JS work owned by `LegacyMap`. Called from
 * `Map.tick` on the ready path, after `stats.begin` and before the
 * dirty-gated draw. */
Map.prototype.tickBefore = function() {

    // promote pending requests to downloads
    this.loader.update();

    // process callbacks from workers
    this.processProcessingTasks();
};


/* Residual post-draw JS work owned by `LegacyMap`. Runs every frame
 * after the dirty-gated draw block; the queued hover/click hit-tests
 * use the canvas that was just drawn. No-op when neither event is
 * queued. */
Map.prototype.tickDeferredEvents = function() {

    if (this.clickEvent == null && this.hoverEvent == null) return;

    var renderer = this.renderer;
    var geometry = renderer.mapCamera.geometry;
    var camPos = geometry.eye;
    var p, result;

    if (this.hoverEvent != null) {

        result = this.hitTestGeoLayers(
            this.hoverEvent[0], this.hoverEvent[1], 'hover');

        var relatedEvents = result[2];

        if (relatedEvents != null) {

            for (var i = 0, li = relatedEvents.length; i < li; i++) {

                var event = relatedEvents[i];
                p = event[1][1];

                var payload = {
                    'feature': event[1][0],
                    'canvas-coords': renderer.project2(
                        event[1][1], geometry.mvp, camPos),
                    'physical-coords': [
                        p[0] + camPos[0],
                        p[1] + camPos[1],
                        p[2] + camPos[2],
                    ],
                    'state': this.hoverEvent[3],
                    'element': result[3],
                };

                if (event[0] === 'enter')
                    this.bus.emit('geo-feature-enter', payload);

                if (event[0] === 'leave')
                    this.bus.emit('geo-feature-leave', payload);
            }
        }

        if (result[1] && result[0] != null) {

            p = result[0][1];
            this.bus.emit('geo-feature-hover', {
                'feature': result[0][0],
                'canvas-coords': renderer.project2(
                    result[0][1], geometry.mvp, camPos),
                'physical-coords': [
                    p[0] + camPos[0],
                    p[1] + camPos[1],
                    p[2] + camPos[2],
                ],
                'state': this.hoverEvent[3],
                'element': result[3],
            });
        }

        // is it persistent event?
        if (this.hoverEvent[2] !== true) {
            this.hoverEvent = null;
        }
    }

    if (this.clickEvent != null) {

        result = this.hitTestGeoLayers(
            this.clickEvent[0], this.clickEvent[1], 'click');

        if (result[1] && result[0] != null) {

            p = result[0][1];
            this.bus.emit('geo-feature-click', {
                'feature': result[0][0],
                'canvas-coords': renderer.project2(
                    result[0][1], geometry.mvp, camPos),
                'physical-coords': [
                    p[0] + camPos[0],
                    p[1] + camPos[1],
                    p[2] + camPos[2],
                ],
                'state': this.clickEvent[2],
                'element': result[3],
            });
        }

        this.clickEvent = null;
    }
};


export default Map;
